import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Globe,
  Loader2,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Platform configurations with icons and colors
const PLATFORM_CONFIG = {
  Meta: {
    name: 'Meta',
    description: 'Facebook & Instagram Ads',
    icon: '📘',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    hoverColor: 'hover:border-blue-400',
    features: ['Age & Gender Breakdowns', 'Country & Region', 'Ad Set & Ad Level', 'Video Metrics']
  },
  DV360: {
    name: 'DV360',
    description: 'Display & Video 360',
    icon: '📺',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    hoverColor: 'hover:border-green-400',
    features: ['Campaign Performance', 'Viewability Metrics', 'Video Completions']
  },
  Mondiads: {
    name: 'Mondiads',
    description: 'Push & Native Ads',
    icon: '🔔',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    hoverColor: 'hover:border-purple-400',
    features: ['Campaign Metrics', 'Click Performance']
  },
  Clickadu: {
    name: 'Clickadu',
    description: 'Pop & Push Network',
    icon: '🎯',
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    hoverColor: 'hover:border-orange-400',
    features: ['Campaign Performance', 'Conversion Tracking']
  },
  Propeller: {
    name: 'Propeller',
    description: 'PropellerAds Network',
    icon: '🚀',
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    hoverColor: 'hover:border-red-400',
    features: ['Campaign Metrics', 'Traffic Analytics']
  }
};

export const PlatformReports = () => {
  const navigate = useNavigate();
  const { user, advertiser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState({});
  const [integrations, setIntegrations] = useState([]);

  // Get user's allowed platforms (for advertisers, filter by their assigned platforms)
  const userAllowedPlatforms = user?.allowed_platforms || [];
  const isAdvertiser = user?.role === 'advertiser';
  const isAdminUser = user?.role === 'super_admin' || user?.role === 'admin';
  
  // Filter PLATFORM_CONFIG based on user's allowed_platforms
  // Admins see all platforms, advertisers only see their allowed platforms
  const filteredPlatforms = Object.entries(PLATFORM_CONFIG).filter(([key]) => {
    if (isAdminUser) return true; // Admins see all platforms
    if (isAdvertiser && userAllowedPlatforms.length > 0) {
      return userAllowedPlatforms.includes(key);
    }
    return true; // Default: show all if no restrictions
  });

  useEffect(() => {
    fetchPlatformData();
  }, [advertiser]);

  const fetchPlatformData = async () => {
    setLoading(true);
    try {
      // Fetch integrations based on user role
      let integrationsData = [];
      
      if (user?.role === 'super_admin' || user?.role === 'admin') {
        // Admins can see all integrations - fetch from all advertisers
        // First try to get all integrations via the integrations endpoint
        try {
          const allIntegrationsRes = await api.get('/integrations');
          integrationsData = allIntegrationsRes.data || [];
        } catch (err) {
          // Fallback: if no global endpoint, try to get from advertiser context
          if (advertiser?.id) {
            const integrationsRes = await api.get(`/advertisers/${advertiser.id}/integrations`);
            integrationsData = integrationsRes.data || [];
          }
        }
      } else if (user?.advertiser_id) {
        // Regular users and advertisers - fetch their advertiser's integrations
        const integrationsRes = await api.get(`/advertisers/${user.advertiser_id}/integrations`);
        integrationsData = integrationsRes.data || [];
      } else if (advertiser?.id) {
        // Fallback to advertiser from context
        const integrationsRes = await api.get(`/advertisers/${advertiser.id}/integrations`);
        integrationsData = integrationsRes.data || [];
      }
      
      setIntegrations(integrationsData);

      // Fetch platform summary for stats
      try {
        const summaryRes = await api.get('/dashboard/platform-summary');
        const stats = {};
        (summaryRes.data || []).forEach(p => {
          stats[p.platform] = {
            spend: p.spend || 0,
            impressions: p.impressions || 0,
            clicks: p.clicks || 0,
            campaigns: p.campaigns || 0
          };
        });
        setPlatformStats(stats);
      } catch (err) {
        console.error('Failed to fetch platform summary:', err);
      }
    } catch (error) {
      console.error('Failed to fetch platform data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformStatus = (platform) => {
    const integration = integrations.find(i => i.platform === platform);
    if (!integration) return 'not_connected';
    // Check both 'status' and 'connection_status' fields for compatibility
    if (integration.status === 'error' || integration.connection_status === 'error') return 'error';
    if (integration.connection_status === 'connected' || integration.is_active) return 'connected';
    return 'connected'; // Default to connected if integration exists
  };

  // Get count of integrations for a platform (useful for super admin to see how many advertisers use it)
  const getPlatformIntegrationCount = (platform) => {
    return integrations.filter(i => i.platform === platform).length;
  };

  const handlePlatformClick = (platform) => {
    navigate(`/performance-report/${platform.toLowerCase()}`);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Performance Reports</h1>
          <p className="text-slate-500 mt-2">
            Select a platform to view detailed performance reports with platform-specific metrics and breakdowns
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-slate-500">Loading platforms...</span>
          </div>
        ) : (
          <>
            {/* Platform Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlatforms.map(([key, config]) => {
                const status = getPlatformStatus(key);
                const stats = platformStats[key] || {};
                const isConnected = status === 'connected';

                return (
                  <Card
                    key={key}
                    className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${config.borderColor} ${config.hoverColor} border-2 ${
                      isConnected ? 'hover:shadow-lg hover:-translate-y-1' : 'opacity-70'
                    }`}
                    onClick={() => isConnected && handlePlatformClick(key)}
                    data-testid={`platform-card-${key.toLowerCase()}`}
                  >
                    {/* Gradient Header */}
                    <div className={`h-2 bg-gradient-to-r ${config.color}`} />
                    
                    <CardContent className="p-6">
                      {/* Platform Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{config.icon}</span>
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{config.name}</h3>
                            <p className="text-sm text-slate-500">{config.description}</p>
                          </div>
                        </div>
                        
                        {/* Status Badge */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          status === 'connected' ? 'bg-green-100 text-green-700' :
                          status === 'error' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {status === 'connected' && <CheckCircle className="w-3 h-3" />}
                          {status === 'error' && <XCircle className="w-3 h-3" />}
                          {status === 'not_connected' && <AlertCircle className="w-3 h-3" />}
                          <span>
                            {status === 'connected' ? (
                              isAdminUser ? `${getPlatformIntegrationCount(key)} Connected` : 'Connected'
                            ) :
                             status === 'error' ? 'Error' : 'Not Connected'}
                          </span>
                        </div>
                      </div>

                      {/* Stats */}
                      {isConnected && (
                        <div className={`grid grid-cols-2 gap-3 p-3 rounded-lg ${config.bgColor} mb-4`}>
                          <div>
                            <p className="text-xs text-slate-500">Spend</p>
                            <p className="font-semibold text-slate-900">{formatCurrency(stats.spend)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Impressions</p>
                            <p className="font-semibold text-slate-900">{formatNumber(stats.impressions)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Clicks</p>
                            <p className="font-semibold text-slate-900">{formatNumber(stats.clicks)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Campaigns</p>
                            <p className="font-semibold text-slate-900">{stats.campaigns || 0}</p>
                          </div>
                        </div>
                      )}

                      {/* Features */}
                      <div className="space-y-1 mb-4">
                        {config.features.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${config.color}`} />
                            {feature}
                          </div>
                        ))}
                      </div>

                      {/* Action Button */}
                      {isConnected ? (
                        <Button 
                          className={`w-full bg-gradient-to-r ${config.color} hover:opacity-90 text-white`}
                          data-testid={`view-reports-${key.toLowerCase()}`}
                        >
                          View Reports
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/my-account');
                            toast.info(`Please add a ${config.name} integration first`);
                          }}
                        >
                          Connect Platform
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default PlatformReports;
